import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { Link } from '../../router/Link';
import {
  Button,
  Eyebrow,
  IconButton,
  JumpOutIcon,
  LoadingStatus,
  Menu,
  Notice,
  Skeleton,
  Spinner,
  ThumbDownIcon,
  ThumbUpIcon,
  cx,
} from '../../ui';
import { Comments } from './Comments';
import { describeKey, keyTargetOf } from './keys';
import { MiniControls } from './MiniControls';
import { MiniNoticeLine } from './MiniNoticeLine';
import type { MiniDrag } from '../../playback/dock';
import { usePlayerSlot } from '../../playback/context';
import { PlayerChrome, SkipIcon, TheaterIcon, type ChromeOptions } from './PlayerChrome';
import { PlaylistPanel } from './PlaylistPanel';
import { SectionHead } from './SectionHead';
import { Recommendations } from './Recommendations';
import { AddToPlaylist } from '../../playlists/AddToPlaylist';
import { WatchLaterButton } from '../../playlists/WatchLaterButton';
import { SubscribeButton } from '../../subscriptions/SubscribeButton';
import { RichText } from '../../richText/RichText';
import { miniPlayerErrorNotice, TIMESTAMP_ROW_ID, watchOnRowOf, type MiniNotice } from './model';
import type { WatchBinding } from './useWatchScreen';
import { useAuthorSubscriberText } from './useAuthorMeta';
import { useCopy } from '../../strings/useCopy';
import styles from './watch.module.css';

/**
 * Renders props. No core imports (non-negotiable 2), no API calls, no URL building — every href on this
 * page arrived already built, and every decision was made in `model.ts` or in core.
 *
 * `onPointerUp` below looks like an exception and is not. Turning a client coordinate into a fraction of
 * an element is something a screen model structurally *cannot* do — `model.ts` has no DOM types by design
 * (R13) — and it never decides what the gesture means: `decideTap` does that, in core, under test. The
 * keyboard's equivalent moved to `keys.ts` when a second listener needed it.
 *
 * The page renders a `<div>` rather than a `<main>`: the shell owns that landmark.
 */

/**
 * The metadata row's glyphs: date, views, likes. The same 24-grid, stroke-1.8 hand as `ui/icons.tsx`
 * — one app, one hand — but drawn here because they are this page's vocabulary, the way the media
 * icons are `PlayerChrome.tsx`'s. 16 px, to sit inside `--vs-text-small`.
 */
function MetaIcon({ children }: { readonly children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={16}
      height={16}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

function CalendarIcon() {
  return (
    <MetaIcon>
      <rect x="4" y="5.5" width="16" height="14.5" rx="1.5" />
      <path d="M8 3.5v3.5M16 3.5v3.5M4 10h16" />
    </MetaIcon>
  );
}

function EyeIcon() {
  return (
    <MetaIcon>
      <path d="M2.8 12S6.2 5.8 12 5.8 21.2 12 21.2 12 17.8 18.2 12 18.2 2.8 12 2.8 12z" />
      <circle cx="12" cy="12" r="2.8" />
    </MetaIcon>
  );
}

/**
 * The actions row's glyphs: same hand as {@link MetaIcon}, at 18 px — the Watch later pill's
 * clock set the size, and every pill on the row now carries a picture of its verb.
 */
function ActionIcon({ children }: { readonly children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={18}
      height={18}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

/** Two chain links: the button copies a *link*, and the label says so too. */
function ShareLinkIcon() {
  return (
    <ActionIcon>
      <path d="M9.7 14.3l4.6-4.6" />
      <path d="M12.9 6.9l1.7-1.7a3.75 3.75 0 0 1 5.3 5.3l-1.7 1.7" />
      <path d="M11.1 17.1l-1.7 1.7a3.75 3.75 0 0 1-5.3-5.3l1.7-1.7" />
    </ActionIcon>
  );
}

/** A dotted list — the chapter rows, timestamps implied by the dots. */
function ChaptersIcon() {
  return (
    <ActionIcon>
      <path d="M9.5 6.5H20M9.5 12H20M9.5 17.5H20" />
      <path d="M4.5 6.5h.01M4.5 12h.01M4.5 17.5h.01" />
    </ActionIcon>
  );
}

/** A numbered list — the playlist panel's own rows, positions and all. */
function PlaylistPanelIcon() {
  return (
    <ActionIcon>
      <path d="M9.5 6.5H20M9.5 12H20M9.5 17.5H20" />
      <path d="M3.6 5.4h1.2v3M3.6 11h2.4l-2.4 3h2.4M3.6 16.6h2.4v3H3.6" />
    </ActionIcon>
  );
}

/** Two rail rows — a thumbnail beside its lines, twice, which is what the panel draws. */
function RecommendationsIcon() {
  return (
    <ActionIcon>
      <rect x="4" y="5" width="7" height="5.5" rx="1" />
      <path d="M13.5 6.5H20M13.5 9h4" />
      <rect x="4" y="13.5" width="7" height="5.5" rx="1" />
      <path d="M13.5 15H20M13.5 17.5h4" />
    </ActionIcon>
  );
}

/**
 * Exported for the /embed screen (2026-08-02), which is this surface wearing the whole
 * viewport: `surfaceClassName` reshapes the box (the watch page's 16:9-with-a-cap geometry is a
 * page concern, not the player's), and `overlay` is the embed's clickable title — rendered inside
 * this element so it survives fullscreen, which is the mode an embedded player lives in.
 */
export function PlayerSurface({
  binding,
  surfaceClassName,
  overlay,
  chromeOptions,
  bar = true,
  mini = null,
  notice = null,
}: {
  binding: WatchBinding;
  surfaceClassName?: string;
  overlay?: ReactNode;
  /** What the page around the bar has taken away from it — /embed is the only caller that does. */
  chromeOptions?: ChromeOptions;
  /**
   * `false` draws no bar at all — `/embed/ID?controls=0`. The picture keeps its tap gesture, its
   * keymap and its big play disc, which is what an embedder asking for no controls still expects.
   */
  bar?: boolean;
  /**
   * Why there is no picture, for the stages that have none — a video the queue reached and the
   * instance will not play (`miniStageNotice`). Non-null only while minimized, since on the watch
   * page the same refusal is the page's own full-size box.
   */
  notice?: MiniNotice | null;
  /**
   * Non-null while this surface is the minimized player. It is the *same element* as the one
   * the watch page had — that is the whole point of `playback/dock.ts` — so what changes here is
   * what rides on it, not what it is.
   *
   * Four things come off, and each for its own reason rather than to save room: the full chrome
   * (`MiniControls` states what fits), the surface's tap and key gestures (a double-tap seek zone is
   * 160 px wide here, the keymap belongs to whatever page the reader is actually on, and a drag is
   * the minimized player's own gesture — `miniSwipe.ts`), the big play disc (it is the picture's
   * "paused" sign, and the whole picture is 320 px wide), and the SponsorBlock toast, whose Unskip is
   * a full-size affordance — the skipping itself keeps running.
   *
   * `follow` goes the other way: it is the *frame's* style, which belongs to `playback/dock.ts`
   * because the frame is not a React node at all. A swipe that moves the whole box has to reach it
   * through here.
   */
  mini?: {
    readonly onMaximize: () => void;
    readonly onClose: () => void;
    /** Whether closing this box also ends a queue — `MiniControls` for what it changes. */
    readonly closesQueue: boolean;
    readonly follow: (drag: MiniDrag | null) => void;
  } | null;
}) {
  const { videoRef, stageRef, externalFrameRef, handlers, transport, chrome, external } = binding;
  const words = useCopy().text.watch.page;
  const miniWords = useCopy().text.watch.mini;
  const skip = binding.skip;
  /*
   * An iframe leg is on (§8's Switch player). The frame is drawn here rather than on the page so it
   * moves with this box into the corner player. On the page it is the whole picture and YouTube's own
   * chrome is the bar, so ours stays off: the bar, the paused disc, the toasts and the load error all
   * describe the player hidden underneath.
   */
  const page = mini === null && external === null;

  /*
   * The one line a minimized player can spare for a video that is not playing, and the two things
   * that can claim it. `notice` goes first: it says the player never started at all, which is a
   * fresher statement about this video than a load error left standing from before the stage moved
   * on — and two alerts in a 320 px box would be one covering the other.
   *
   * Both arms carry the kind of statement they are making as well as its words, because below `md`
   * the line is drawn as a glyph — `MiniNoticeLine`, and `model.ts` for why that decision is not the
   * view's to make.
   */
  const miniLine =
    mini === null || external !== null
      ? null
      : (notice ?? (binding.playerError === null ? null : miniPlayerErrorNotice()));

  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>): void => {
    const handled = handlers.key(describeKey(event.nativeEvent), keyTargetOf(event.target));
    // Only when something was actually done with it: an unmapped key must keep scrolling the page
    // and Tab must keep moving focus. Marking the press is also what keeps the document-level
    // listener (`keys.ts`) off the presses this one took.
    if (handled) event.preventDefault();
  };

  const onPointerUp = (event: ReactPointerEvent<HTMLDivElement>): void => {
    const box = event.currentTarget.getBoundingClientRect();
    if (box.width <= 0) return;
    handlers.surfaceUp(
      event.clientX,
      event.clientY,
      (event.clientX - box.left) / box.width,
      // A pen counts as fine: it points at a spot, so click-to-play is the right reading of it.
      event.pointerType === 'touch' ? 'coarse' : 'fine',
      event.button,
    );
  };

  return (
    // `tabIndex` and the handler on *this* element, never on `document` (§7): the page has other
    // keyboard consumers, and a document-level listener would take `←`/`→` away from all of them.
    <div
      className={cx(
        styles.player,
        mini !== null && styles.mini,
        page && !chrome.visible && styles.idle,
        // `surfaceClassName` is /embed's own "undo the watch page's geometry" seam (see its doc
        // comment above) — the same reason its box has no business inheriting the 16:9 ratio has it
        // no business inheriting this override either; the iframe's own `object-fit: contain` box
        // already takes any shape without one.
        page && surfaceClassName === undefined && binding.videoPortrait && styles.portrait,
        surfaceClassName,
      )}
      // The cast is for the custom property, which `CSSProperties` cannot name — read by `.portrait`
      // below `md` so the box takes the picture's own shape instead of assuming 16∶9.
      style={
        !page || surfaceClassName !== undefined || binding.videoAspectRatio === null
          ? undefined
          : ({ '--video-ar': binding.videoAspectRatio } as CSSProperties)
      }
      ref={stageRef}
      // Not focusable while minimized: the surface's key handler is §7's keymap, and a Tab stop that
      // swallows `←`/`→` has no business in the corner of a page the reader is reading.
      tabIndex={mini === null ? 0 : undefined}
      role="region"
      aria-label={mini === null ? words.playerLabel : miniWords.label}
      onKeyDown={mini === null ? onKeyDown : undefined}
      onPointerDown={
        mini === null
          ? (event) => handlers.surfaceDown(event.clientX, event.clientY, event.button)
          : undefined
      }
      onPointerUp={mini === null ? onPointerUp : undefined}
      onPointerMove={mini === null ? handlers.pointerActivity : undefined}
      /*
       * `:focus-visible`, not `:focus`. Focus alone keeps the chrome up forever after a *mouse* click,
       * because clicking Play focuses the Play button and the bar then covers the bottom of the video
       * for the rest of the session. The distinction the auto-hide actually wants is "somebody is
       * navigating by keyboard and would lose the focus ring" — which is the same distinction §9's
       * focus model already draws, and which the browser computes for us.
       */
      /*
       * Both halves off while minimized, and not merely because there is no auto-hiding bar to keep
       * up: pressing Maximize *removes* the control that had focus, so no `blur` follows and the flag
       * would be left `true` — the watch page would then arrive with its chrome pinned visible for
       * the rest of the video.
       */
      onFocus={
        mini !== null
          ? undefined
          : (event) =>
              handlers.setFocusInside(
                event.target instanceof Element && event.target.matches(':focus-visible'),
              )
      }
      onBlur={mini !== null ? undefined : () => handlers.setFocusInside(false)}
    >
      {/*
       * Kept while an iframe leg is on, so switching back resumes the same element. Absent only when
       * no player was ever built (a failed stage the reader switched away from), where an empty
       * element would still claim `videoRef`.
       */}
      {external !== null && binding.stage.kind !== 'player' ? null : (
        <video className={styles.video} ref={videoRef} playsInline />
      )}

      {/*
       * Minimized, the frame takes no pointer: the corner's own controls and its swipe are on the
       * surface, and a cross-origin frame would swallow both.
       */}
      {external === null ? null : (
        <iframe
          className={cx(styles.externalFrame, mini !== null && styles.externalFrameMini)}
          src={external.url}
          ref={externalFrameRef}
          title={words.externalFrameTitle[external.provider]}
          allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
          allowFullScreen
        />
      )}

      {/*
       * The sign that the picture is paused: the disc in the middle of the frame, on every face of
       * the player that draws a picture at all. Not a control — the surface is what takes the press,
       * and play/pause has its button in the bar — so `aria-hidden`.
       */}
      {!page || transport.playing ? null : (
        <div className={styles.bigPlay} aria-hidden="true">
          <svg viewBox="0 0 24 24" width="34" height="34" focusable="false">
            <path fill="currentColor" d="M7 4.5 19.5 12 7 19.5z" />
          </svg>
        </div>
      )}

      {/*
       * The double-tap answer: a disc on the half of the picture the finger is on, counting the
       * run's total. `key` on the **run's start**, so a run of quick taps keeps one element whose
       * number goes up — keyed on each press it re-mounted, and a run read as a flashing disc rather
       * than as a counter. Its fade is a class instead, added once the run has been still long enough
       * (`skipDiscLeaving`), because adding a class starts an animation and re-rendering one does not.
       * Purely decorative (`aria-hidden`): the seek itself is announced by the scrub slider's value,
       * and a disc that spoke too would say it twice.
       */}
      {!page || skip === null || skip.origin !== 'gesture' ? null : (
        <div
          key={skip.stamp}
          className={cx(
            styles.skipDisc,
            skip.direction === 'back' ? styles.skipDiscBack : styles.skipDiscForward,
            skip.leaving && styles.skipDiscLeaving,
          )}
          aria-hidden="true"
        >
          <SkipIcon forward={skip.direction === 'forward'} size={26} />
          <span className={styles.skipDiscText}>{skip.discText}</span>
        </div>
      )}

      {/*
       * The same run, when a *control* asked for it — the bar's two buttons and their keyboard twins.
       * The SponsorBlock toast's clothes and its exact place, because it is the same kind of sentence
       * about the same kind of jump; that one wins the spot when both apply, since it carries an
       * Unskip to press and this one is only a statement.
       */}
      {!page || skip === null || skip.origin !== 'control' || binding.sponsor.toast !== null ? null : (
        <div className={styles.skipToast} role="status">
          {skip.text}
        </div>
      )}

      {/*
       * The SponsorBlock toast — a sibling of the chrome, deliberately: the chrome auto-hides and a
       * hidden bar is `inert`, while "Skipped Sponsor · Unskip" must be clickable exactly when the
       * pointer has been idle long enough for the bar to have faded. Inside the fullscreen element,
       * so it survives fullscreen. Both gesture ends are stopped for the chrome's own reason — a
       * click on Unskip is not also a tap on the video.
       */}
      {!page || binding.sponsor.toast === null ? null : (
        <div
          className={styles.sponsorToast}
          role="status"
          onPointerDown={(event) => event.stopPropagation()}
          onPointerUp={(event) => event.stopPropagation()}
        >
          <span>{binding.sponsor.toast.text}</span>
          <Button size="sm" variant="media" onClick={binding.sponsor.toastAction}>
            {binding.sponsor.toast.actionLabel}
          </Button>
        </div>
      )}

      {overlay}

      {mini === null ? (
        bar && page ? <PlayerChrome binding={binding} options={chromeOptions} /> : null
      ) : (
        <MiniControls
          binding={binding}
          onMaximize={mini.onMaximize}
          onClose={mini.onClose}
          closesQueue={mini.closesQueue}
          follow={mini.follow}
        />
      )}

      {/*
       * Minimized, the box says in one line why there is no picture — the stage's own refusal, or a
       * load that failed under it ({@link miniLine}). Not one line because the box is small but
       * because every way out of a failure is full-size — Try again, the Watch on menu — and a
       * 320 px corner that says nothing at all is the worse failure: a picture that stopped with no
       * reason given reads as the app having quietly died. `role="alert"`, since it either arrives
       * with the box or appears mid-session, and both are news — and it is what speaks the sentence
       * below `md`, where the line itself is a glyph on the tile.
       */}
      {miniLine === null ? null : (
        <div className={styles.miniError} role="alert">
          <MiniNoticeLine notice={miniLine} />
        </div>
      )}

      {/*
       * A failed *load* (2026-08-02) on the page: centered on the picture, over the chrome. It
       * stays up until a load succeeds — `retryPlayer` only flips the button to its busy face — and
       * `role="alert"` because it appears mid-session, where the old below-the-player notice was
       * announced too. Both gesture ends stop for the chrome's reason: a press on Try again is not
       * also a tap on the video.
       */}
      {!page || binding.playerError === null ? null : (
        <div
          className={styles.playerErrorOverlay}
          role="alert"
          onPointerDown={(event) => event.stopPropagation()}
          onPointerUp={(event) => event.stopPropagation()}
        >
          <StageFailure
            binding={binding}
            title={words.playerFailedTitle}
            detail={binding.playerError}
            retry={{ onRetry: handlers.retryPlayer, busy: binding.playerRetrying }}
          />
        </div>
      )}
    </div>
  );
}

/**
 * A sentence across the middle of the player's box: a heading, a line under it, and whatever the
 * caller puts below the two.
 *
 * The geometry rather than the grammar, which is why it takes no binding. {@link StageFailure} is
 * the grammar — everything the player area *refuses to play* — and this is the part a state with
 * nothing to offer can wear as well: a tab that has handed its videos to another one
 * ({@link HandedOverView}) has neither a retry nor anywhere else to watch a video that is no longer
 * here, and an actions row with nothing in it would be a band of empty space under the sentence.
 */
function StageMessage({
  title,
  detail,
  children,
}: {
  title: string;
  detail: string;
  children?: ReactNode;
}) {
  return (
    <div className={styles.stageFailure}>
      <h2 className={styles.failureTitle}>{title}</h2>
      <p className={styles.failureDetail}>{detail}</p>
      {children}
    </div>
  );
}

/**
 * One failure grammar for everything the player area can refuse to play (2026-08-02):
 * centered on both axes in the player's own box, the Watch on menu — an instance failing says
 * nothing about whether YouTube still has the video, so the way out belongs on every one of these —
 * and, where a second attempt could land differently, a Try again whose face goes busy while it is
 * in flight. `retry` is `null` for the deterministic refusals and for the premiere, which is a date
 * rather than a defect. Exported for /embed, whose viewport plays the part of the box.
 */
export function StageFailure({
  binding,
  title,
  detail,
  retry,
}: {
  binding: WatchBinding;
  title: string;
  detail: string;
  retry: { readonly onRetry: () => void; readonly busy: boolean } | null;
}) {
  const retryWord = useCopy().text.shell.state.retry;

  return (
    <StageMessage title={title} detail={detail}>
      <div className={styles.failureActions}>
        {retry === null ? null : (
          <Button onClick={retry.onRetry} disabled={retry.busy}>
            {retry.busy ? <Spinner small /> : null}
            {retryWord}
          </Button>
        )}
        {/* The whole way out, centred under the sentence the way the box centres everything else
            on this axis — a broken video is exactly when it earns its place. */}
        <div className={styles.failureDestinations}>
          <WatchOn binding={binding} placement="bottom-center" />
        </div>
      </div>
    </StageMessage>
  );
}

/**
 * The watch page of a tab that gave its videos to another one and stayed open (`tabs/`).
 *
 * The player's own box with a sentence across it, which is the shape every other "there is nothing
 * to play here" wears — and deliberately nothing else. There is no Try again, because nothing
 * failed; no Watch on, because the video is not gone from the world but from *this tab*; and none
 * of the page below the player, because its title, its description and its comments would all be
 * about a video this tab is no longer watching.
 *
 * It stands until the reader closes the tab or navigates, and the second one ends it by itself:
 * `tabs/store.ts` keeps the handover against the address it happened at.
 */
export function HandedOverView() {
  const words = useCopy().text.tabs.handedOver;
  return (
    <div className={styles.watch}>
      {/* 16∶9 as a number rather than a token, the way every other stage box states its shape —
          there is no video here to have one of its own. */}
      <div className={styles.stageArea} style={{ '--stage-ar': 16 / 9 } as CSSProperties}>
        <div className={styles.stage}>
          <div className={styles.stageFailureBox}>
            <StageMessage title={words.title} detail={words.detail} />
          </div>
        </div>
      </div>
    </div>
  );
}

function Stage({ binding }: { binding: WatchBinding }) {
  const words = useCopy().text.watch.page;
  const { stage, videoRef, playAttempt } = binding;
  /*
   * The player's box is not rendered here. It is one element with a lifetime longer than this
   * page's — that is what lets a video keep playing while the reader navigates away — so this arm
   * puts an empty slot on the page and `playback/dock.ts` moves the surface into it. The slot is
   * `display: contents`, so `.player` stays a direct child of `.stage` and every rule written that
   * way keeps matching.
   */
  const playerSlot = usePlayerSlot();

  /*
   * The player-swap escape hatch's two iframe legs (2026-08-03) — YouTube always, PeerTube's
   * "embedded" mode. Checked ahead of `stage.kind` because it overrides every one of its arms: the
   * reader who switched away from a *failed* Visious load (§8's whole point of offering this from
   * `StageFailure` too) still gets the working alternative, not the error box. `stage.kind ===
   * 'loading'` is the one arm this never fires under — the swap rows need the video's own
   * title to search or embed with, and that is not known until the details request lands, by which
   * point `stage.kind` has already moved on. PeerTube's "integrated" mode is deliberately absent
   * here: it is a different manifest in *this same* player, not a second surface, so it falls
   * through to the ordinary `'player'` case below unchanged.
   *
   * The iframe itself is drawn by `PlayerSurface`, not here (2026-10-15): this arm is the slot, so
   * the frame rides the docked box into the corner player when the reader leaves the page. Drawn on
   * the page it went with the page, and the corner showed the paused player that had been under it.
   */
  if (binding.external !== null) {
    return (
      <div className={styles.stage}>
        <div className={styles.playerSlot} ref={playerSlot} />
      </div>
    );
  }

  switch (stage.kind) {
    case 'loading':
      /*
       * The player's own box, not a centred panel with a spinner in it.
       *
       * The panel was the shortest thing to write and it was the worst-behaved loading state in the
       * app: it is a few lines tall, so the whole page — title, byline, description, chapters, rail —
       * arrived in one jump when the response landed, shoving the video down the screen past wherever
       * the reader had already put their pointer. Everything below is the same geometry the real page
       * has, so the content lands where its skeleton was. The rail and the comments are absent on
       * purpose: both are separate requests that have not been made yet, and drawing twelve grey rows
       * for something that may legitimately turn out to be empty is a promise rather than a placeholder.
       */
      return (
        <div className={styles.stage} aria-busy>
          <LoadingStatus>{words.loadingVideo}</LoadingStatus>
          {/* By ratio rather than by height, which is how the real player box is sized too — the same
              shape `FeedGridSkeleton` draws for a card. The width and the radius are the stylesheet's
              (`.playerSkeleton`) rather than props: below `md` the box goes full-bleed, and a rule
              cannot override an inline style. */}
          <Skeleton height="auto" className={styles.playerSkeleton} />
        </div>
      );

    /*
     * The three refusals wear the player's own box (2026-08-02): same geometry, same black,
     * message centered on both axes — the light panel they used to get made the page reshape
     * itself around exactly the reader who already has a problem. Every error offers the jump-out
     * (an instance answering 404, or handing back another request's body — API reference §3 — says
     * nothing about whether YouTube still has the video; the menu is built from the route, so it
     * works with no video and even with no instance). Try again comes with it only where a second
     * request could answer differently: `WatchStage` says which those are, and a premiere is a date
     * rather than a defect, so it keeps the box but drops the button.
     */
    case 'problem':
    case 'unplayable':
      return (
        <div className={styles.stage}>
          <div className={styles.stageFailureBox}>
            <StageFailure
              binding={binding}
              title={stage.title}
              detail={stage.detail}
              retry={
                stage.kind === 'problem' && stage.retryable
                  ? { onRetry: binding.handlers.retry, busy: binding.retrying }
                  : null
              }
            />
          </div>
        </div>
      );

    case 'upcoming':
      return (
        <div className={styles.stage}>
          <div className={styles.stageFailureBox}>
            <StageFailure
              binding={binding}
              title={words.premiereHeading}
              detail={
                stage.premiereText === null
                  ? words.premiereUnknown
                  : words.premiereAt(stage.premiereText)
              }
              retry={null}
            />
          </div>
        </div>
      );

    case 'progressive':
      return (
        <div className={styles.stage}>
          {/* The one place the browser's own chrome is correct: without MSE there is nothing for
              custom controls to drive but a plain element, and §7 says to state the limitation.
              This arm is never minimized, and needs no guard to stay that way: with no `PlayerPort`
              the transport never reports `playing`, and only a playing video is retained. */}
          <video
            className={cx(styles.video, binding.videoPortrait && styles.portrait)}
            style={
              binding.videoAspectRatio === null
                ? undefined
                : ({ '--video-ar': binding.videoAspectRatio } as CSSProperties)
            }
            ref={videoRef}
            src={stage.url}
            controls
            playsInline
          />
          <Notice>{stage.notice}</Notice>
        </div>
      );

    case 'player':
      return (
        <div className={styles.stage}>
          {/* A failed load's error is *inside* the surface now (`playerErrorOverlay`) — centered
              on the picture rather than appended under it. */}
          <div className={styles.playerSlot} ref={playerSlot} />
          {playAttempt === 'blocked' ? (
            <Notice live>{words.playBlocked}</Notice>
          ) : null}
          {playAttempt === 'muted-fallback' ? (
            <Notice live>{words.mutedFallback}</Notice>
          ) : null}
        </div>
      );
  }
}

/**
 * §8's jump-out and the player escape hatch, one "Watch on" dropdown since they merged: the swaps
 * under one heading, the links under another, the timestamp row under both.
 *
 * The Open site items are real `<a>`s with `rel="noreferrer"` — `Menu` renders `href` items that
 * way for exactly this caller's reason: without it the browser tells YouTube which Visious page the
 * reader came from, which is most of what a privacy-preserving frontend exists to avoid. They
 * navigate the current tab (2026-08-02): following a jump-out means "keep watching over there".
 *
 * **Which rows close the menu is the row's identity, not its label** (`watchOnRowOf`). A link is
 * already being followed by the browser and a YouTube swap is instant, so both close. The timestamp
 * stays open exactly like Share's — it modifies where the *next* link lands, and a menu that
 * vanished on the modifier would cost a reopen to use it. The PeerTube swap stays open too: its row
 * is clickable only once the search resolved `found`, and the integrated leg then has one more
 * request in flight that the row goes `busy` again for.
 *
 * Rendered by `StageFailure` too, where the whole way out belongs. `placement` passes straight
 * through to `Menu` — the failure box centres it, the actions row keeps the default start alignment
 * — and `Menu` itself flips a clipped popover upward (2026-08-03), so this component carries none
 * of that measurement.
 *
 * `onOpen` starts PeerTube's search (2026-08-06), the way `ShareMenu`'s does — the two dropdowns
 * share one search, so opening either fills the other's rows as well. The trigger wears the accent
 * (`triggerActive`) once a fallback has actually replaced Visious's own player.
 */
export function WatchOn({
  binding,
  placement,
}: {
  binding: WatchBinding;
  placement?: 'bottom-start' | 'bottom-center';
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className={styles.jumpOut}>
      <Menu
        menu={binding.watchOn.menu}
        variant="pill"
        triggerActive={binding.watchOn.active}
        triggerIcon={<JumpOutIcon />}
        placement={placement}
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (next) binding.watchOn.onOpen();
        }}
        onSelect={(itemId) => {
          binding.watchOn.onSelect(itemId);
          const row = watchOnRowOf(itemId);
          if (row?.kind === 'open' || (row?.kind === 'switch' && row.target !== 'peertube')) {
            setOpen(false);
          }
        }}
      />
    </div>
  );
}

/**
 * The Share dropdown: a destination row copies its link and closes; the timestamp row toggles
 * and deliberately stays open — it modifies what the *next* press copies, and a menu that vanished on
 * the modifier would cost a reopen to use it. The status span is always mounted so the transient
 * "Link copied" is announced as a change rather than arriving as a new live region.
 *
 * `onOpen` starts PeerTube's search (2026-08-06), the way `JumpOut`'s and `SwitchTo`'s do — the three
 * dropdowns share one search, so opening any of them fills the other two's rows as well.
 */
function ShareMenu({ binding }: { binding: WatchBinding }) {
  const [open, setOpen] = useState(false);

  return (
    <div className={styles.share}>
      <Menu
        menu={binding.share.menu}
        variant="pill"
        triggerIcon={<ShareLinkIcon />}
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (next) binding.share.onOpen();
        }}
        onSelect={(itemId) => {
          binding.share.onSelect(itemId);
          if (itemId !== TIMESTAMP_ROW_ID) setOpen(false);
        }}
      />
      <span className={styles.shareFeedback} role="status">
        {binding.share.feedback ?? ''}
      </span>
    </div>
  );
}

/**
 * The title, the byline and the first lines of the description, as boxes.
 *
 * `aria-hidden`, with {@link LoadingStatus} above the stage saying the one sentence out loud — the rule
 * every list on this app follows, and it matters more here because these are four shapes rather than
 * twelve identical ones and a screen reader would read them as four blanks.
 */
function MetaSkeleton() {
  return (
    <div className={cx(styles.meta, styles.metaSkeleton)} aria-hidden="true">
      <Skeleton text width="72%" className={styles.titleSkeleton} />
      <div className={styles.byline}>
        <Skeleton width="44px" height="44px" radius="var(--vs-radius-pill)" />
        <Skeleton text width="14ch" />
      </div>
      <div className={styles.descriptionSkeleton}>
        <Skeleton text width="100%" />
        <Skeleton text width="94%" />
        <Skeleton text width="61%" />
      </div>
    </div>
  );
}

/**
 * The chapter list, as a panel with its own header — since the layout pass it sits beside the
 * video (the side column, above the recommendations), and it is hideable. Beside the video the way
 * back lives in the actions row under the metadata, so a hidden panel leaves the side column
 * genuinely empty. **Below the description** (`stacked`, the narrow arrangement) the panel wears
 * the comments header instead — a real heading with its own Show/Hide on the row's far end — and a
 * hidden panel keeps the header here, `shown` deciding only the body.
 * The toggle's visible word is "Hide"/"Show"; the accessible name says hide *what*.
 *
 * **The bar's chapter name presses through to this list** (2026-08-25): the handler shows the panel
 * and raises `binding.revealChapter`, and the row the playhead is inside brings itself into view
 * here — this is the only place holding the rows, and only the panel knows which element that is.
 * `block: 'nearest'` scrolls both the list's own overflow box and, below the breakpoint where the
 * panel sits under the description, the page — and neither of them when the row is already in view.
 * The flag is lowered again the moment it is acted on, so showing the panel by hand later is not
 * read as a second reveal.
 */
function ChaptersPanel({
  binding,
  stacked,
  shown,
}: {
  binding: WatchBinding;
  stacked: boolean;
  shown: boolean;
}) {
  const words = useCopy().text.watch.page;
  const video = binding.video;
  const currentRow = useRef<HTMLButtonElement | null>(null);
  const { revealChapter, handlers } = binding;

  useEffect(() => {
    if (!revealChapter) return;
    currentRow.current?.scrollIntoView({ block: 'nearest' });
    handlers.chapterRevealed();
  }, [handlers, revealChapter]);

  if (video === null || video.chapters.length === 0) return null;

  return (
    <section className={styles.chapters}>
      {stacked ? (
        <SectionHead
          className={styles.stackedHead}
          headingClassName={styles.stackedHeading}
          heading={words.chapters}
          shown={shown}
          onToggle={binding.handlers.toggleChapters}
        >
          <Button
            variant="subtle"
            className={styles.stackedToggle}
            label={shown ? words.hideChapters : words.showChapters}
            ariaExpanded={shown}
            onClick={binding.handlers.toggleChapters}
          >
            {shown ? words.hide : words.show}
          </Button>
        </SectionHead>
      ) : (
        <div className={styles.panelHead}>
          <Eyebrow>{words.chapters}</Eyebrow>
          <Button variant="subtle" label={words.hideChapters} onClick={binding.handlers.toggleChapters}>
            {words.hide}
          </Button>
        </div>
      )}
      {!shown ? null : (
        <ol className={styles.chapterList}>
          {video.chapters.map((chapter, index) => {
            const badge = binding.sponsor.badges[index] ?? null;
            const current = index === binding.transport.chapterIndex;
            return (
              <li key={chapter.startSec}>
                <button
                  type="button"
                  ref={current ? currentRow : null}
                  className={cx(styles.chapter, current && styles.chapterCurrent)}
                  aria-current={current ? 'true' : undefined}
                  onClick={() => binding.handlers.seekTo(chapter.startSec)}
                >
                  <span className={styles.chapterTime}>{chapter.startText}</span>
                  <span>{chapter.title}</span>
                  {/* The ≥75 %-covered badge: the dot carries the category's colour as data,
                      the word carries the meaning — never colour alone. */}
                  {badge === null ? null : (
                    <span className={styles.chapterBadge} title={badge.title}>
                      <span
                        className={styles.chapterBadgeDot}
                        style={{ backgroundColor: badge.color }}
                        aria-hidden="true"
                      />
                      {badge.label}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}

export function WatchView({ binding }: { binding: WatchBinding }) {
  const words = useCopy().text.watch.page;
  const chromeWords = useCopy().text.watch.chrome;
  const railWords = useCopy().text.watch.rail;
  /* The way-back pill says the same thing the panel's own head says, which is "playlist" for either
     list: the side column has one list and this page names it one way (`PlaylistPanel`'s `PanelCopy`).
     A pill that read "Show the queue" beside a heading that reads "Playlist" would be two names for
     one press. */
  const panelWords = useCopy().text.watch.playlist;
  const video = binding.video;
  const subscriberText = useAuthorSubscriberText(video?.channelId ?? null);

  /*
   * What the side column will actually draw, and in which clothes. At the wide breakpoint the
   * panels sit beside the video (or, in theater, beside the metadata and comments) and a hidden one
   * is absent — its way back is a pill in the actions row, so the column can be genuinely empty and
   * the player take the full width (`.expanded`). Below the breakpoint the panels are *sections
   * under the description* instead: each keeps a comments-style header with its own Show/Hide, and
   * the pills disappear with the column they pointed at. While the video is still loading
   * nothing is knowable, and the class stays off so the skeleton keeps the geometry the loaded page
   * is most likely to have.
   */
  const wide = binding.wideViewport;
  const hasChapters = (video?.chapters.length ?? 0) > 0;
  const hasRail = binding.rail.length > 0;
  // The playlist panel exists whenever the route named a list — before the first page lands, too,
  // so the column does not reshape itself around a section that is one request away.
  const hasPlaylist = binding.playlist !== null;
  const showChapters = hasChapters && (!wide || binding.layout.chapters);
  const showRecommendations = hasRail && (!wide || binding.layout.recommendations);
  const showPlaylist = hasPlaylist && (!wide || binding.layout.playlist);
  const expanded =
    video !== null &&
    !(binding.layout.chapters && hasChapters) &&
    !(binding.layout.recommendations && hasRail) &&
    !(binding.layout.playlist && hasPlaylist);

  return (
    /*
     * Four grid children — stage, metadata, the side column, comments — because they arrange three
     * ways. Narrow stacks them in reading order. Wide puts the side column (chapters over
     * recommendations) beside the *video*, which is why the stage and its metadata are separate
     * children. Theater spans the stage across both columns; an empty side column collapses the
     * columns entirely and the player takes the full width (`.expanded`).
     */
    <div
      className={cx(
        styles.watch,
        binding.layout.theater && styles.theater,
        expanded && styles.expanded,
      )}
    >
      {/*
       * The stage's shape, as one number on the box that holds it (`binding.stageAspectRatio`, core's
       * `stageShape`): `watch.module.css` reads `--stage-ar` as the player's own ratio in every mode,
       * and theater reads it a second time as the multiplier that turns the height cap into this
       * box's width. Written unconditionally, saying 16∶9 whenever nothing else applies.
       */}
      <div
        className={cx(styles.stageArea, binding.stageCrop && styles.cropped)}
        style={{ '--stage-ar': binding.stageAspectRatio } as CSSProperties}
      >
        <Stage binding={binding} />
      </div>

      <div className={styles.metaArea}>
        {video === null && binding.stage.kind === 'loading' ? <MetaSkeleton /> : null}

        {/*
         * The zero-byte failure's remainder (`binding.degraded`): the id is well-formed and the
         * instance did not refuse the video, so the acts that need only the id keep working under
         * the failure box — Watch later, Save to playlist, Share. Watch on is not repeated here,
         * because the failure box above already carries it as the way out of every refusal. Watch
         * later goes without a title: the pill face never speaks one, and the page has none to
         * give.
         */}
        {video === null && binding.degraded ? (
          <article className={styles.meta}>
            <div className={styles.videoActions}>
              <WatchLaterButton face="pill" videoId={binding.videoId} />
              <AddToPlaylist
                videoId={binding.videoId}
                signInHref={binding.saveToPlaylist.signInHref}
              />
              <ShareMenu binding={binding} />
            </div>
          </article>
        ) : null}

        {video === null ? null : (
          <article className={styles.meta}>
            {/*
             * The embedded legs' own theater toggle (2026-08-03): `PlayerChrome`'s bar — and its own
             * toggle with it — is not on screen at all while `binding.external !== null` (the surface
             * draws the iframe and leaves its own chrome off), so this is the only
             * way to reach the mode without switching back to Visious first. Same breakpoint as the
             * bar's own button (`.embeddedTheaterButton` mirrors `.theaterButton`'s reasoning: below
             * `md` the picture is already full-bleed in every mode, so the control would do nothing).
             */}
            <div className={styles.titleRow}>
              <h1 className={styles.title}>{video.title}</h1>
              {binding.external === null ? null : (
                <IconButton
                  variant="ghost"
                  className={styles.embeddedTheaterButton}
                  onClick={binding.handlers.toggleTheater}
                  label={binding.layout.theater ? chromeWords.exitTheater : chromeWords.theater}
                >
                  <TheaterIcon active={binding.layout.theater} />
                </IconButton>
              )}
            </div>

            <div className={styles.byline}>
              {video.authorAvatarUrl === null ? (
                <div className={cx(styles.avatar, styles.avatarBlank)} aria-hidden="true" />
              ) : (
                <img className={styles.avatar} src={video.authorAvatarUrl} alt="" width={44} height={44} />
              )}
              <div className={styles.bylineText}>
                <span className={styles.bylineName}>
                  {video.authorHref === null ? (
                    <span className={styles.author}>{video.author}</span>
                  ) : (
                    <Link className={styles.author} href={video.authorHref}>
                      {video.author}
                    </Link>
                  )}
                  {video.authorVerified ? (
                    <span className={styles.verified} title={words.verified}>
                      ✓
                    </span>
                  ) : null}
                </span>
                {/* The channel page's own figure, hydrated through the R14 cache — absent,
                    never zero, while nothing is cached (`useAuthorSubscriberText`). */}
                {subscriberText === null ? null : (
                  <span className={styles.subscribers}>{subscriberText}</span>
                )}
              </div>
              {/* The sub/unsub next to the channel badge. Renders nothing on a collaboration
                  byline, which has no single channel to subscribe to. */}
              <SubscribeButton channelId={video.channelId} />
              {/*
                * Icons instead of label words: the word survives as the hover `title` (carrying the
                * exact figure where one exists) and as visually-hidden text, so a screen reader still
                * hears "1.2M views" while the row prints only the number. Date first, then views,
                * then likes — and no duration: the player itself states that.
                */}
              <div className={styles.counters}>
                {video.publishedText === null ? null : (
                  <span className={styles.counter} title={video.publishedTitle ?? undefined}>
                    <CalendarIcon />
                    {video.publishedText}
                  </span>
                )}
                {video.viewCountText === null ? null : (
                  <span
                    className={styles.counter}
                    title={words.views(video.viewCountTitle ?? video.viewCountText)}
                  >
                    <EyeIcon />
                    <span aria-hidden="true">{video.viewCountText}</span>
                    <span className={styles.srOnly}>{words.views(video.viewCountText)}</span>
                  </span>
                )}
                {video.likeCountText === null ? null : (
                  <span
                    className={styles.counter}
                    title={words.likes(video.likeCountTitle ?? video.likeCountText)}
                  >
                    {/* The comments' own thumb: the two counts are the same field under two
                        names, so they are one glyph now — `ui/icons.tsx` says why it moved there. */}
                    <ThumbUpIcon />
                    <span aria-hidden="true">{video.likeCountText}</span>
                    <span className={styles.srOnly}>{words.likes(video.likeCountText)}</span>
                  </span>
                )}
                {binding.dislikeCountText === null ? null : (
                  <span
                    className={styles.counter}
                    title={words.dislikes(binding.dislikeCountTitle ?? binding.dislikeCountText)}
                  >
                    <ThumbDownIcon />
                    <span aria-hidden="true">{binding.dislikeCountText}</span>
                    <span className={styles.srOnly}>{words.dislikes(binding.dislikeCountText)}</span>
                  </span>
                )}
              </div>
            </div>

            <div className={styles.videoActions}>
              {/* Watch later first, then Save to playlist, then the dropdowns. */}
              <WatchLaterButton face="pill" videoId={binding.videoId} videoTitle={video.title} />
              {/* Fetches nothing until it is opened: the owned-playlist list costs ~1.2 KB per entry
                  across the whole collection and has no summary form (`useOwnedPlaylists.ts`). */}
              <AddToPlaylist
                videoId={binding.videoId}
                signInHref={binding.saveToPlaylist.signInHref}
              />
              <WatchOn binding={binding} />
              <ShareMenu binding={binding} />
              {/* The way back to a hidden panel, on this row's far end — never in the side column,
                  which must stay empty so the player can span the width the panel ceded. Wide only:
                  below the breakpoint each panel keeps its own header and toggle. The pills
                  sit in the column's own order — chapters, playlist, rail — so a row of them points
                  at the sections in the order they will come back. */}
              {wide &&
              ((video.chapters.length > 0 && !binding.layout.chapters) ||
                (binding.rail.length > 0 && !binding.layout.recommendations) ||
                (hasPlaylist && !binding.layout.playlist)) ? (
                <div className={styles.panelToggles}>
                  {video.chapters.length > 0 && !binding.layout.chapters ? (
                    <Button variant="pill" size="sm" onClick={binding.handlers.toggleChapters}>
                      <ChaptersIcon />
                      {words.showChapters}
                    </Button>
                  ) : null}
                  {hasPlaylist && !binding.layout.playlist ? (
                    <Button variant="pill" size="sm" onClick={binding.handlers.togglePlaylist}>
                      <PlaylistPanelIcon />
                      {panelWords.showList}
                    </Button>
                  ) : null}
                  {binding.rail.length > 0 && !binding.layout.recommendations ? (
                    <Button variant="pill" size="sm" onClick={binding.handlers.toggleRecommendations}>
                      <RecommendationsIcon />
                      {railWords.showRecommendations}
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </div>

            {/* Nothing at all for an empty description — no frame and no Show more,
                because both would be furniture around a blank. */}
            {video.hasDescription ? (
              <section className={styles.description}>
                <RichText
                  className={binding.descriptionExpanded ? undefined : styles.descriptionClamped}
                  nodes={video.descriptionNodes}
                  onSeek={binding.handlers.seekTo}
                />
                <Button
                  variant="subtle"
                  className={styles.descriptionToggle}
                  onClick={binding.handlers.toggleDescription}
                >
                  {binding.descriptionExpanded ? words.showLess : words.showMore}
                </Button>
              </section>
            ) : null}
          </article>
        )}
      </div>

      {/*
       * The side column: chapters over recommendations. Wide, only the panels currently shown — a
       * hidden panel's way back lives in the actions row, so hiding both leaves this element
       * `:empty` and the page collapses to one column (`.expanded` above). Narrow, the same element
       * stacks below the description and every panel with content stays, wearing the comments
       * header with its own toggle (`stacked`).
       */}
      <div className={styles.side}>
        {showChapters ? (
          <ChaptersPanel binding={binding} stacked={!wide} shown={!wide ? binding.layout.chapters : true} />
        ) : null}
        {/* Below chapters: a chapter list is about the video on screen and belongs next to it,
            where the playlist is about what comes after — and directly above the rail, the other
            list of things to play next. */}
        {showPlaylist && binding.playlist !== null ? (
          <PlaylistPanel
            binding={binding.playlist}
            kind={binding.playlistKind}
            layout={{
              onHide: binding.handlers.togglePlaylist,
              autoplay: binding.listAutoplay.on,
              onAutoplayChange: binding.listAutoplay.set,
            }}
            onClear={binding.clearList}
            keep={binding.listKeep}
            stacked={!wide}
            shown={!wide ? binding.layout.playlist : true}
          />
        ) : null}
        {showRecommendations ? (
          <Recommendations
            items={binding.rail}
            onHide={binding.handlers.toggleRecommendations}
            stacked={!wide}
            shown={!wide ? binding.layout.recommendations : true}
          />
        ) : null}
      </div>

      {/*
       * No comment section when the video itself did not load. §6's `404 {"error":"Comments not
       * found."}` legitimately means "no comments" (1e), but under "Video not found" it renders as
       * "Comments are turned off for this video" — a confident statement about a video the instance has
       * just said it does not have. The request still goes out in parallel; only the sentence is
       * withheld. The degraded failure is the exception: a zero-byte `200` is not the instance
       * saying the video is gone, so there the comments' own answer stands.
       */}
      {binding.stage.kind === 'problem' && !binding.degraded ? null : (
        <Comments
          binding={binding.comments}
          onSeek={binding.handlers.seekTo}
          shown={binding.layout.comments}
          onToggleShown={binding.handlers.toggleComments}
          className={styles.commentsArea}
        />
      )}
    </div>
  );
}
